<script>
import {mapGetters} from 'vuex'

import Translatable from '@src/mixins/translatable.js'
import Likable from '@src/mixins/likable.js'
import Resizable from '@src/mixins/resizable.js'

export default {
  mixins: [
    Translatable,
    Likable,
    Resizable
  ],
  props: {
    isOpened: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      steps: {
        ATTRACTIONS_SELECTION_STEP: 'attractionsSelection',
        CALCULATION_STEP: 'calculation',
        TOTAL_SAVINGS_STEP: 'total',
      },
      step: 'attractionsSelection',
      currentCardTab: 1,
      tabs: {
        oneDayPass: 1,
        twoDayPass: 2,
        threeDayPass: 3,
        fourDayPass: 4,
        fiveDayPass: 5,
        sixDayPass: 6,
        sevenDayPass: 7,
        tenDayPass: 10,
      }
    }
  },
  computed: {
    ...mapGetters('attractions', [
      'likedAttractions',
      'getAttraction'
    ]),
    ...mapGetters('calculator', [
      'cards'
    ]),
    titleTruncationLimit() {
      return this.desktopScreen ? 23 : 100
    },
    currentStep() {
      if (this.likedAttractions.length) {
        return this.step
      } else {
        return 'attractionsSelection'
      }
    },
    stepsOrder() {
      return [this.steps.ATTRACTIONS_SELECTION_STEP, this.steps.CALCULATION_STEP, this.steps.TOTAL_SAVINGS_STEP]
    },
    currentCard() {
      return this.cards.filter(card => card.id === this.currentCardTab)
    },
    totalCost() {
      return this.removeGroupFromCalculations().map(attraction => {
        const studentsCardPriceWithCoolpass = parseFloat(this.getStudentsPrice(attraction))
        // cards
        return this.currentCard.reduce((sum, card) => {
          return attraction.priceAdult ? (
              sum + (
                parseFloat(attraction.priceAdult) * card.adultCardCount +
                studentsCardPriceWithCoolpass * card.childCardCount
              )
          ) : sum
        }, 0)
      }).reduce((accumulator, current) => accumulator + current, 0).toFixed(0)
    },
    discountCost() {
      // cards
      const cardsPrice = this.currentCard.reduce((sum, card) => {
        return sum + (
            card.childCardCount * card.priceChildFixedCZK +
            card.adultCardCount * card.priceAdultFixedCZK
          )
      }, 0)

      const calcBenefitSum = this.removeGroupFromCalculations().map(attraction => {
        const studentsCardPriceWithCoolpass = parseFloat(this.getStudentsBenefit(attraction))
        // cards
        return this.currentCard.reduce((sum, card) => {
          return attraction.benefitAdult ? (
              sum + (
                parseFloat(attraction.benefitAdult) * card.adultCardCount +
                studentsCardPriceWithCoolpass * card.childCardCount
              )
          ) : sum
        }, 0)
      }).reduce((accumulator, current) => accumulator + current, 0)

      return (calcBenefitSum + cardsPrice).toFixed(0)
    },
    totalSavings() {
      const savedAmount = parseFloat(this.totalCost) - parseFloat(this.discountCost)
      return savedAmount > 0 ? (savedAmount).toFixed(0) : (0).toFixed(0)
    },
    discountedAttractions() {
      return this.likedAttractions.filter(attraction => +attraction.priceAdult && !isNaN(+attraction.priceAdult))
    },
    specialGiftAttractions() {
      return this.likedAttractions.filter(attraction => !+attraction.priceAdult)
    },
    openedCalculator() {
      if (this.desktopScreen) {
        return true
      }
      return this.likedAttractions.length
    }
  },
  methods: {
    next() {
      if (!this.isOpened) {
        this.toggleCalculator()
      }

      this.step = this.stepsOrder[this.stepsOrder.indexOf(this.currentStep) + 1]
    },
    redirectToEshop() {
      this.$router.push({name: 'eshop'})
      this.toggleCalculator()
    },
    incrementCount(cardId, cardType) {
      this.$store.commit('calculator/INCREMENT_CARD_COUNT', {cardId, cardType})
    },
    decrementCount(cardId, cardType) {
      this.$store.commit('calculator/DECREMENT_CARD_COUNT', {cardId, cardType})
    },
    getStudentsPrice(attraction) {
      return attraction.priceChild ? attraction.priceChild : attraction.priceAdult
    },
    getStudentsBenefit(attraction) {
      return attraction.benefitChild ? attraction.benefitChild : attraction.benefitAdult
    },
    removeGroupFromCalculations() {
      return [...this.specialGiftAttractions, ...this.discountedAttractions].filter(attraction => {
        return !(this.containsGroup(attraction._id) && attraction.parentAttraction)
      })
    },
    toggleCalculator() {
      this.$emit('toggle-calculator-mobile')
    },
    removeAttractionLike(attraction) {
      this.removeLike(attraction._id)
      !this.likedAttractions.length && this.toggleCalculator()
    },
    clearAll() {
      this.$store.commit('attractions/CLEAR_ALL_LIKED_ATTRACTIONS')
      this.toggleCalculator()
    },
    cardName(card) {
      return card
      .replace('Day', this.getTranslatedContent('CALCULATOR_day_label'))
      .replace('Prague Card', this.getTranslatedContent('CALCULATOR_pass_label'))
    },
  }
}
</script>

<template>
    <div
      v-if="openedCalculator"
      class="calculator-container"
      :class="likedAttractions.length ? 'populated' : ''"
    >
      <div :class="isOpened ? 'calculator-header-opened' : 'calculator-header'" @click="toggleCalculator">
        <span class="header-text">{{ getTranslatedContent('CALCULATOR_title') }}
          <span v-if="!isOpened && !desktopScreen" class="header-text-count">{{ likedAttractions.length }}</span>
        </span>
        <div v-if="isOpened && !desktopScreen" class="cross"></div>
      </div>
      <div class="liked-attractions" :class="isOpened ? 'liked-attractions' : 'disabled'">
        <div v-if="likedAttractions.length" class="liked-attractions-title">
          <p>{{ getTranslatedContent('CALCULATOR_NORMAL_ADULT_PRICE') }}</p>
        </div>
        <div v-if="likedAttractions.length" class="attractions-list">
          <ul>
            <li v-for="attraction in discountedAttractions" :key="attraction._id">
              <span class="attractions-list__title">{{ getTranslatedContent('title', attraction.content).toLowerCase() | truncate(titleTruncationLimit) }}</span>
              <span v-if="!attraction.parentAttraction" class="price">{{ attraction.priceAdult | KC }}</span>
              <div v-if="!attraction.parentAttraction" class="delete-btn" @click="removeAttractionLike(attraction)"></div>
            </li>
            <li v-for="attraction in specialGiftAttractions" :key="attraction._id">
              <span class="attractions-list__title">{{ getTranslatedContent('title', attraction.content).toLowerCase() | truncate(titleTruncationLimit) }}</span>
              <span v-if="!attraction.parentAttraction" class="price">{{ attraction.benefit }}</span>
              <div v-if="!attraction.parentAttraction" class="delete-btn" @click="removeAttractionLike(attraction)"></div>
            </li>
          </ul>
        </div>
        <div v-else class="calculator-tip">
          {{ getTranslatedContent('CALCULATOR_tip') }}
        </div>
      </div>
      <div v-if="currentStep === steps.ATTRACTIONS_SELECTION_STEP" class="btn-wrapper">
        <div v-if="likedAttractions.length" class="clear-btn-container">
          <div
            v-if="desktopScreen || isOpened"
            class="clear-btn"
            @click="clearAll"
          >
            <span class="clear">{{ getTranslatedContent('CALCULATOR_CLEAR') }}</span>
          </div>
        </div>
        <div
          v-if="likedAttractions.length"
          class="check-my-savings-btn active"
          :class="!isOpened ? 'closed' : ''"
          @click="next">
          <span>{{ getTranslatedContent('CALCULATOR_check_my_savings_btn') }}</span>
        </div>
        <div v-else class="check-my-savings-btn disabled">
          <span>{{ getTranslatedContent('CALCULATOR_check_my_savings_btn') }}</span>
        </div>
      </div>
      <div
        v-if="currentStep !== steps.ATTRACTIONS_SELECTION_STEP && likedAttractions.length"
        class="cards-container"
        :class="isOpened ? 'cards-container' : 'disabled'"
      >
        <div class="cards-tabs">
          <div
            v-for="card in cards"
            :key="card.id"
            class="tab"
            :class="card.id === currentCardTab ? 'active' : ''"
            @click="currentCardTab = card.id"
          >
          <span>{{ cardName(card.name.default) }}</span>
        </div>
      </div>
      <div
        v-for="card in cards"
        v-show="currentCardTab === card.id"
        :key="card.id" class="calculator-body"
        :class="currentStep === steps.CALCULATION_STEP ? 'active' : ''"
      >
        <div class="calculator-row">
          <span class="card-label">{{ getTranslatedContent('CALCULATOR_ADULT') }}</span>
          <div class="decrement" @click="decrementCount(card.id, 'adult')"></div>
          <div class="counter"><span>{{ card.adultCardCount }}</span></div>
          <div class="increment" @click="incrementCount(card.id, 'adult')"></div>
        </div>
        <div class="calculator-row">
          <span class="card-label">{{ getTranslatedContent('STUDENT/CHILD') }}</span>
          <div class="decrement" @click="decrementCount(card.id, 'child')"></div>
          <div class="counter"><span>{{ card.childCardCount }}</span></div>
          <div class="increment" @click="incrementCount(card.id, 'child')"></div>
        </div>
      </div>
      <div v-if="currentStep === steps.CALCULATION_STEP" class="clear-btn-container">
        <div
          v-if="desktopScreen || isOpened"
          class="clear-btn"
          @click="clearAll"
        >
          <span class="clear">{{ getTranslatedContent('CALCULATOR_CLEAR') }}</span>
        </div>
      </div>
      <div
        v-if="currentStep === steps.CALCULATION_STEP"
        class="continue-btn"
        @click="next"
      >
        <span>{{ getTranslatedContent('CALCULATOR_continue_btn') }}</span>
      </div>
    </div>
      <div
        v-if="currentStep === steps.CALCULATION_STEP && !isOpened && !desktopScreen"
        class="continue-btn closed"
        @click="next"
      >
        <span>{{ getTranslatedContent('CALCULATOR_continue_btn') }}</span>
      </div>
    <div
      v-if="currentStep === steps.TOTAL_SAVINGS_STEP && likedAttractions.length"
      class="total-savings-container"
      :class="isOpened ? 'total-savings-container' : 'disabled'"
    >
      <div class="total-savings-body">
        <div class="total-cost">
          <span class="label">{{ getTranslatedContent('CALCULATOR_total_entry_prices_cost') }}</span>
          <span class="price">{{ totalCost | KC }}</span>
        </div>
        <div class="benefit-cost">
          <div class="total-price-with-coolpass">
            <span class="label">{{ getTranslatedContent('CALCULATOR_cost_with_coolpass') }}</span>
            <span class="price">{{ discountCost | KC }}</span>
          </div>
          <span class="separator"></span>
          <div class="savings-with-coolpass">
            <span class="label">{{ getTranslatedContent('CALCULATOR_you_save_label') }}</span>
            <span class="savings">{{ totalSavings | KC }}</span>
          </div>
        </div>
      </div>
      <div class="clear-btn-container">
        <div
          v-if="desktopScreen || isOpened"
          class="clear-btn"
          @click="clearAll"
        >
          <span class="clear">{{ getTranslatedContent('CALCULATOR_CLEAR') }}</span>
        </div>
      </div>
      <div class="continue-btn" @click="redirectToEshop">
        <span>{{ getTranslatedContent('CALCULATOR_order_your_coolpass') }}</span>
      </div>
    </div>
      <div
        v-if="currentStep === steps.TOTAL_SAVINGS_STEP && !isOpened && !desktopScreen"
        class="continue-btn closed"
        @click="redirectToEshop">
        <span>{{ getTranslatedContent('CALCULATOR_order_your_coolpass') }}</span>
      </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.calculator-container {
  min-height: 320px;
  height: auto;
  width: 270px;

  position: sticky;
  top: 10px;

  & .calculator-header, .calculator-header-opened {
    border-radius: 10px 10px 0 0;
    background-color: $dark-blue;
    width: 270px;
    height: 60px;
    display: flex;
    justify-content: center;
    align-items: center;

    & .header-text {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
      font-size: 14px;
      line-height: 18px;
      text-align: center;
      color: #FFFFFF;
      text-transform: uppercase;
    }
  }

  & .liked-attractions {
    background: $light-gray;
    // height: 185px;
    height: 367px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    & .liked-attractions-title {
      height: 20px;
      text-align: right;
      padding-right: 10px;
      margin-bottom: 3px;

      p {
        font-family: Open Sans, sans-serif;
        font-style: normal;
        font-weight: normal;
        font-size: 12px;
        line-height: 20px;
        text-transform: capitalize;
        color: $hover-blue;
      }
    }

    & .calculator-tip {
      font-family: Open Sans Semibold, sans-serif;
      font-weight: 600;
      font-size: 14px;
      line-height: 18px;
      text-align: center;
      color: $orange;
      margin: auto 16px;
    }
  }

  &.populated {
    & .liked-attractions {
      box-sizing: border-box;
      padding-left: 4px;
      padding-right: 4px;
      padding-top: 2px;
    }
  }

  & .cards-container {
    margin-top: 5px;
    background-color: $light-gray;

    & .cards-tabs {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      width: fit-content;
      box-sizing: border-box;
      width: 100%;
      padding-top: 10px;
      padding-bottom: 10px;

      padding-left: 5px;
      padding-right: 4px;

      display: flex;
      flex-direction: row;
      border-radius: 5px;
      user-select: none;

      & .tab {
        // flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        margin-right: 1.45px;
        background: #FFFFFF;
        padding-left: 10px;
        padding-right: 10px;
        cursor: pointer;

        &:last-child {
          margin-right: 0;
          border-radius: 0 5px 5px 0;
        }

        &:first-child {
          border-radius: 5px 0 0 5px;
        }

        & span {
          font-family: Open Sans, sans-serif;
          font-style: normal;
          font-weight: 600;
          font-size: 12px;
          line-height: 16px;
          text-align: center;
          text-transform: uppercase;
          color: $dark-blue;
          // opacity: 0.8;
        }

        &.active {
          background: $orange;

          & span {
            color: white;
            opacity: 1;
          }
        }
      }
    }

    & .calculator-body.active {
      // height: 150px;
      height: 95px;
    }

    & .calculator-body {
      // height: 119px;
      height: 95px;

      & .calculator-row {
        display: flex;
        align-items: center;
        margin-top: 15px;
        height: 35px;
        width: 100%;
        padding-left: 15px;
        padding-right: 10px;
        box-sizing: border-box;

        & span {
          font-family: Open Sans Bold, sans-serif;
          font-style: normal;
          font-weight: bold;
          font-size: 14px;
          line-height: 18px;
          color: $dark-blue;
        }

        & .decrement,
        & .increment,
        & .counter {
          background: white;
          height: 35px;
        }

        & .decrement {
          width: 38px;
          box-shadow: 3px 0 2px rgba(207, 206, 221, 0.08);
          border-radius: 4px 0 0 4px;
          background-image: url("../../assets/icons/minus.svg");
          background-position: center;
          background-repeat: no-repeat;
          z-index: 1;
          margin-left: auto;
          cursor: pointer;
        }

        & .increment {
          width: 38px;
          background-image: url("../../assets/icons/plus.svg");
          background-position: center;
          background-repeat: no-repeat;
          box-shadow: -3px 0 2px rgba(207, 206, 221, 0.08);
          border-radius: 0 4px 4px 0;
          cursor: pointer;
        }

        & .counter {
          width: 50px;
          display: flex;
          justify-content: center;
          align-items: center;

          & span {
            font-family: Open Sans Bold, sans-serif;
            font-weight: bold;
            font-size: 14px;
            line-height: 18px;
            color: $dark-blue;
          }
        }
      }
    }
  }

  & .total-savings-container {
    margin-top: 5px;

    & .total-savings-body {
      box-sizing: border-box;
      width: 270px;
      height: 180px;
      background: $light-gray;
      margin-top: 5px;
      padding-top: 10px;
      padding-right: 3px;
      padding-left: 4px;

      & .total-cost {
        width: 100%;
        height: 56px;
        padding-left: 10px;
        padding-right: 14px;
        box-sizing: border-box;
        background: white;
        display: flex;
        align-items: center;

        & .label {
          max-width: 119px;
        }
      }

      .label {
        font-family: Open Sans Bold, sans-serif;
        font-weight: bold;
        font-size: 14px;
        line-height: 18px;
        color: $dark-blue;
        // opacity: 0.8;
      }

      .price {
        font-family: Open Sans Bold, sans-serif;
        font-weight: bold;
        font-size: 14px;
        line-height: 18px;
        text-align: center;
        // text-transform: uppercase;
        color: $dark-blue;

        margin-left: auto;
      }

      & .benefit-cost {
        display: flex;
        flex-direction: column;
        background: white;
        margin-top: 5px;
        padding-left: 10px;
        padding-right: 16px;
        padding-top: 12px;
        box-sizing: border-box;
        height: 95px;

        & .total-price-with-coolpass {
          width: 100%;
          display: flex;

          & .label {
            max-width: 142px;
          }
        }

        & .separator {
          height: 1px;
          width: 248px;
          background: rgba(218, 75, 7, 0.7);
          margin: 12px auto 4px;
        }

        & .savings-with-coolpass {
          display: flex;

          & .label {
            font-family: Open Sans Bold, sans-serif;
            font-weight: bold;
            font-size: 18px;
            line-height: 23px;
            color: $dark-blue;
            // opacity: 0.8;
          }

          & .savings {
            font-family: Open Sans Bold, sans-serif;
            font-weight: bold;
            font-size: 18px;
            line-height: 23px;
            text-align: right;
            color: $green;
            margin-left: auto;
          }
        }
      }
    }
  }
}

.continue-btn {
  background: $orange;
  height: 60px;
  width: 270px;
  border-radius: 0 0 10px 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  & span {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 15px;
    line-height: 19px;
    text-transform: uppercase;
    color: #FFFFFF;
  }

  &:hover {
    background: $hover-orange;
  }
}

.check-my-savings-btn {
  width: 270px;
  height: 60px;
  border-radius: 0 0 10px 10px;
  display: flex;
  justify-content: center;
  align-items: center;

  &.disabled {
    background: $dark-gray;
  }

  &.active {
    background: $orange;
    cursor: pointer;

    &:hover {
      background: $hover-orange;
    }
  }

  & span {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 15px;
    line-height: 19px;
    text-transform: uppercase;
    color: #FFFFFF;
  }
}

.clear-btn-container {
  background: $light-gray;
  padding: 0 4px 4px;
}

.clear-btn {
  width: 100%;
  height: 32px;
  background: $white;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  & span {
    font-family: Open Sans;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 20px;
    text-decoration-line: underline;
    color: $orange;

    &:hover {
      color: $hover-orange;
    }
  }
}

.attractions-list {
  box-sizing: border-box;
  width: 100%;
  overflow: auto;

  & ul {
    & li {
      box-sizing: border-box;
      width: 100%;
      min-height: 31px;
      background: white;
      margin-bottom: 5px;
      display: flex;
      align-items: center;
      padding: 8px 4px 7px 10px;

      &:last-child {
        margin-bottom: 5px;
      }

      .attractions-list__title {
        // font-family: Open Sans Bold, sans-serif;
        // font-weight: bold;
        font-family: Open Sans, sans-serif;
        font-weight: normal;
        font-size: 12px;
        line-height: 16px;
        color: $dark-blue;
        text-transform: capitalize;
        // width: 111px;
        margin-right: 5px;
      }

      .price {
        // font-family: Open Sans Bold, sans-serif;
        // font-weight: bold;
        font-family: Open Sans, sans-serif;
        font-weight: normal;
        font-size: 12px;
        line-height: 16px;
        text-align: right;
        margin-left: auto;
        min-width: 50px;
        color: $gray;
      }

      .delete-btn {
        width: 16px;
        height: 16px;
        transform: scale(0.70);

        background: url("../../assets/icons/x.svg") center no-repeat;
        cursor: pointer;

        margin-left: 3px;
      }
    }
  }
}

@media only screen and (min-width: $mobile-screen-width) and (max-width: $screen-width-xs + 1) {
  .cross {
    background: url("../../assets/icons/calculator-close.svg") center no-repeat;
    width: 17px;
    height: 17px;
    position: absolute;
    right: 8px;
    top: 8px;
  }

  .calculator-container {
    width: 100%;
    height: 100%;
    overflow-y: scroll;
    display: flex;
    flex-direction: column;

    & .calculator-header-opened {
      width: 100%;
      border-radius: 0;
      min-height: 96px;

      & .header-text {
        font-size: 18px;
        line-height: 23px;
        width: 100%;
      }
    }

    & .calculator-header {
      width: 100%;
      border-radius: 0;
      background: $white;
      height: 50px;

      & .header-text {
        font-family: Open Sans, sans-serif;
        font-weight: normal;
        font-size: 14px;
        line-height: 23px;
        color: $orange;
        text-transform: none;

        & .header-text-count {
          font-family: Open Sans Bold, sans-serif;
          font-style: normal;
          font-weight: bold;
          font-size: 14px;
          line-height: 18px;
          color: $green;
        }
      }
    }

    & .cards-container {
      margin-top: 0;

      & .calculator-body.active {
        height: 100px;
      }

      & .calculator-body {
        height: 100px;
      }

      &.disabled {
        display: none;
      }

      & .cards-tabs {
        padding-top: 0;
      }
    }

    & .total-savings-container {
      margin-top: 0;

      &.disabled {
        display: none;
      }

      & .total-savings-body {
        margin-top: 0;
        padding-top: 0;
        width: 100%;
        height: 190px;

        & .benefit-cost {
          & .separator {
            width: 100%;
            margin: 12px 0 4px;
          }
        }
      }
    }

    & .liked-attractions {
      flex: 1;
      height: auto;

      &.disabled {
        display: none;
      }
    }

    &.populated {
      & .liked-attractions {
        padding: 0;
      }
    }
  }

  .check-my-savings-btn, .continue-btn {
    width: 100%;
    border-radius: 0;

    &.closed {
      height: 40px;

      & span {
        font-family: Open Sans, sans-serif;
        font-weight: normal;
        font-size: 12px;
        line-height: 16px
      }
    }
  }

  .attractions-list {
    overflow: hidden;

    & ul {
      padding: 0 4px;
      & li {
        &:last-child {
          margin-bottom: 15px;
        }
        .attractions-list__title {
          min-width: 130px;
        }
        .price {
          margin-left: auto;
        }
        .delete-btn {
          margin-left: 10px;
          margin-right: 1px;
        }
      }
    }
  }
}
</style>
